import React, { useState } from 'react';

/**
 * Recorrido virtual con Matterport embebido inline, estilo visual premium.
 *
 * Props:
 *  - modelName  : 'Fernanda' | 'Mariana'
 *  - matterport : URL completa del tour 3D
 *  - poster     : imagen de fachada para el estado inicial
 *  - accent     : color string
 *  - specs      : array opcional de { icon, label, sub } para tabla lateral
 */
export default function ModeloTour3D({ modelName, matterport, poster, accent = '#f6802c', specs = [] }) {
  const [playing, setPlaying] = useState(false);
  const hasSpecs = Array.isArray(specs) && specs.length > 0;

  return (
    <section id="recorrido-vr" className="mt3d-section" data-testid="mt3d-section">
      <div className="page-padding">
        <div className="container-large">
          <div className="mt3d-head">
            <div className="text-label" style={{ color: 'rgba(255,255,255,0.55)' }}>recorrido virtual · 360°</div>
            <h2 className="sm-h2">
              Camina por el <span className="serif-accent">Modelo {modelName}.</span>
            </h2>
            <p style={{ color: 'rgba(247,252,255,0.85)', maxWidth: '60ch', margin: '0.5rem 0 0' }}>
              Recorre cada habitación desde donde estés. Navega con las marcas circulares,
              activa la vista de planta o toma el control de la presentación automática.
            </p>
          </div>

          <div className={`mt3d-layout${hasSpecs ? ' mt3d-layout-split' : ''}`}>
            <div className="mt3d-stage-col">
            <div className="mt3d-stage" data-testid="mt3d-stage" style={{ '--stage-accent': accent }}>
              {!playing ? (
                <button
                  type="button"
                  className="mt3d-poster"
                  onClick={() => setPlaying(true)}
                  style={{ backgroundImage: `url(${poster})` }}
                  data-testid="mt3d-play"
                  aria-label={`Reproducir tour de ${modelName}`}
                >
                  <div className="mt3d-poster-overlay" />
                  <div className="mt3d-play-circle">
                    <span className="icon-font">play_arrow</span>
                  </div>
                  <div className="mt3d-poster-meta">
                    <div className="mt3d-poster-eyebrow">Recorrido virtual</div>
                    <div className="mt3d-poster-name">{modelName}</div>
                  </div>
                  <div className="mt3d-360-badge">
                    <span className="icon-font">view_in_ar</span>
                    <span>Tour 360°</span>
                  </div>
                </button>
              ) : (
                <div className="mt3d-iframe-wrap">
                  <button
                    className="mt3d-close"
                    onClick={() => setPlaying(false)}
                    aria-label="Detener tour"
                    data-testid="mt3d-close"
                  >
                    <span className="icon-font">close</span>
                  </button>
                  <iframe
                    src={matterport}
                    title={`Tour 3D ${modelName}`}
                    allow="autoplay; fullscreen; xr-spatial-tracking; accelerometer; gyroscope"
                    allowFullScreen
                  />
                </div>
              )}
            </div>

              <div className="mt3d-perks">
                <div className="mt3d-perk">
                  <span className="icon-font">hd</span>
                  <span>Calidad HD</span>
                </div>
                <div className="mt3d-perk">
                  <span className="icon-font">phone_iphone</span>
                  <span>Compatible con celular</span>
                </div>
                <div className="mt3d-perk">
                  <span className="icon-font">vrpano</span>
                  <span>Vista 360° inmersiva</span>
                </div>
                <div className="mt3d-perk">
                  <span className="icon-font">schedule</span>
                  <span>3 minutos por modelo</span>
                </div>
              </div>
            </div>

            {hasSpecs && (
              <aside className="mt3d-specs" data-testid="mt3d-specs">
                <div className="mt3d-specs-eyebrow">Modelo {modelName}</div>
                <div className="mt3d-specs-title">Datos clave</div>
                <ul className="mt3d-specs-list">
                  {specs.map((s, i) => (
                    <li key={i} className="mt3d-specs-item" data-testid={`mt3d-spec-${i}`}>
                      <span className="icon-font mt3d-specs-icon">{s.icon}</span>
                      <div className="mt3d-specs-text">
                        <div className="mt3d-specs-label">{s.label}</div>
                        <div className="mt3d-specs-sub">{s.sub}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              </aside>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
